import React, { useState, useEffect } from 'react';
import {
    supabase_get_clients,
    supabase_insert_client,
    supabase_update_client,
    supabase_delete_client,
    supabase_get_client_branding,
    supabase_update_client_branding
} from '../services/supabaseService';
import { Client } from '../types';
import {
    Building2, LogIn, ExternalLink, Shield, Plus, MoreHorizontal,
    Edit, Trash2, Palette, Check, X, Search, Loader2, Upload, Wand2,
    CreditCard, BarChart3, Calendar, ToggleLeft, ToggleRight, Settings, Users, ArrowUpRight, LayoutTemplate, Save, ChevronRight, Globe, Tag, MapPin, User
} from 'lucide-react';
import { supabase_upload_client_logo, supabase_get_site_config } from '../services/supabaseService';
import ClientDetailModal from './ClientDetailModal';
import { AccessProfile } from '../types';

interface ClientsManagerProps {
    user?: any;
    onLoginAs: (clientId: string, clientName: string, portalTestEmail?: string) => void;
    initialClientId?: string | null;
}

const ClientsManager: React.FC<ClientsManagerProps> = ({ user, onLoginAs, initialClientId }) => {
    const [clients, setClients] = useState<Client[]>([]);
    const [accessProfiles, setAccessProfiles] = useState<AccessProfile[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Dashboard & Branding Modals
    const [showClientModal, setShowClientModal] = useState(false);
    const [showBrandingModal, setShowBrandingModal] = useState(false);
    const [editingClient, setEditingClient] = useState<Client | null>(null);
    const [brandingClient, setBrandingClient] = useState<Client | null>(null);

    const [brandingData, setBrandingData] = useState<any>({ primaryColor: '#e51b23', secondaryColor: '#1e293b', logoUrl: '' });
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        loadClients();
        loadProfiles();
    }, []);

    // Automatically open the targeted client when initialClientId changes
    useEffect(() => {
        if (!initialClientId) return;
        
        // Wait for clients to be loaded
        if (loading && clients.length === 0) return;
        
        let c = clients.find(cl => cl.id === initialClientId);
        if (c) {
            setSearchTerm('');
            setEditingClient(c);
            setShowClientModal(true);
        } else {
            // Unlikely, but if it wasn't loaded in the main list, fetch it directly
            supabase_get_clients(initialClientId).then(data => {
                if (data && data.length > 0) {
                     const fetchedClient = data.find((f: Client) => f.id === initialClientId);
                     if (fetchedClient) {
                         setSearchTerm('');
                         setEditingClient(fetchedClient);
                         setShowClientModal(true);
                     }
                }
            }).catch(ignored => {});
        }
    }, [initialClientId, clients, loading]);

    const loadProfiles = async () => {
        const config = await supabase_get_site_config();
        if (config?.accessProfiles) {
            setAccessProfiles(config.accessProfiles);
        }
    };

    const loadClients = async () => {
        setLoading(true);
        try {
            const clientIdParam = user?.role !== 'ADMIN' && user?.clientId ? user.clientId : undefined;
            const data = await supabase_get_clients(clientIdParam);
            const clientsData = data || [];
            
            // Admins only see root Tool Clients (Tenants), not end-clients
            if (user?.role === 'ADMIN') {
                setClients(clientsData.filter(c => !c.parent_client_id));
            } else {
                setClients(clientsData);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    // Client CRUD
    const handleAddClient = () => {
        setEditingClient(null);
        setShowClientModal(true);
    };

    const handleEditClient = (client: Client) => {
        setEditingClient(client);
        setShowClientModal(true);
    };

    const handleDeleteClient = async (id: string) => {
        if (window.confirm('Weet je zeker dat je deze klant wilt verwijderen? Dit kan niet ongedaan worden gemaakt.')) {
            try {
                await supabase_delete_client(id);
                loadClients();
            } catch (e) {
                console.error("Delete failed", e);
                alert("Kon klant niet verwijderen.");
            }
        }
    };



    // Branding Logic
    const openBrandingModal = async (client: Client) => {
        setBrandingClient(client);
        setShowBrandingModal(true);
        setIsSaving(true); // Loading state for fetching
        const config = await supabase_get_client_branding(client.id);
        if (config) {
            setBrandingData(config);
        } else {
            setBrandingData({ primaryColor: '#e51b23', secondaryColor: '#1e293b', logoUrl: '' });
        }
        setIsSaving(false);
    };

    const saveBranding = async () => {
        if (!brandingClient) return;
        setIsSaving(true);
        try {
            await supabase_update_client_branding(brandingClient.id, brandingData);
            setShowBrandingModal(false);
        } catch (e) {
            console.error(e);
            alert("Kon branding niet opslaan.");
        } finally {
            setIsSaving(false);
        }
    };

    // Helper: Extract color from image
    const extractDominantColor = (imageSrc: string): Promise<string | null> => {
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = "Anonymous";
            img.src = imageSrc;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (!ctx) return resolve(null);

                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);

                // Sample center pixels
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const data = imageData.data;
                const colorCounts: { [key: string]: number } = {};
                let maxCount = 0;
                let dominantColor = '';

                // Simple quantization (step 40)
                for (let i = 0; i < data.length; i += 40) {
                    const r = data[i];
                    const g = data[i + 1];
                    const b = data[i + 2];
                    const a = data[i + 3];

                    if (a < 128) continue; // Skip transparent
                    if (r > 240 && g > 240 && b > 240) continue; // Skip white

                    const hex = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
                    colorCounts[hex] = (colorCounts[hex] || 0) + 1;

                    if (colorCounts[hex] > maxCount) {
                        maxCount = colorCounts[hex];
                        dominantColor = hex;
                    }
                }
                resolve(dominantColor || null);
            };
            img.onerror = () => resolve(null);
        });
    };

    const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0] || !brandingClient) return;
        const file = e.target.files[0];

        setIsSaving(true);
        try {
            const publicUrl = await supabase_upload_client_logo(brandingClient.id, file);
            setBrandingData({ ...brandingData, logoUrl: publicUrl });
        } catch (err: any) {
            console.error("Upload failed", err);
            alert("Upload mislukt: " + (err.message || 'Check connection'));
        } finally {
            setIsSaving(false);
        }
    };

    const applyMagicColors = async () => {
        if (!brandingData.logoUrl) return;
        setIsSaving(true);
        try {
            const color = await extractDominantColor(brandingData.logoUrl);
            if (color) {
                setBrandingData({ ...brandingData, primaryColor: color });
            } else {
                alert("Kon geen dominante kleur vinden.");
            }
        } finally {
            setIsSaving(false);
        }
    };

    const filteredClients = clients.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.industry?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="flex flex-col h-[calc(100vh-64px)] bg-white animate-fadeIn">
            {/* Header Area */}
            <div className="px-6 lg:px-10 pt-3 pb-1">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
                    <div>
                        <h1 className="text-xl font-black text-slate-900 tracking-tight leading-tight flex items-center gap-2">
                            <Building2 className="text-[#e51b23] shrink-0" size={24} /> Klanten Beheer
                        </h1>
                        <p className="text-slate-500 text-xs font-medium mt-1">Beheer accounts, instellingen en whitelabel branding.</p>
                    </div>
                    <div className="flex items-center gap-3 w-full md:w-auto">
                        <div className="relative flex-1 md:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                            <input
                                type="text"
                                placeholder="Zoek klant..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#e51b23] shadow-sm"
                            />
                        </div>
                        <button
                            onClick={handleAddClient}
                            className="bg-[#e51b23] hover:bg-red-700 text-white px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-2 shadow-lg transition-all active:scale-95"
                        >
                            <Plus size={16} /> <span className="hidden sm:inline">Nieuwe Klant</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Table Area */}
            <div className="flex-1 overflow-hidden relative w-full px-4 lg:px-6 pb-2 pt-2">
                <div className="flex flex-1 gap-6 overflow-hidden h-full">
                    <div className="flex flex-col gap-4 transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] w-full">
                        <div className="flex-1 overflow-x-auto bg-white rounded-[1.5rem] shadow-sm border border-slate-200 flex flex-col relative w-full mt-2">
                            <div className="flex-1 overflow-y-auto custom-scrollbar bg-white">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200">
                            <tr>
                                <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Organisatie</th>
                                <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest hidden md:table-cell">Type & Plan</th>
                                <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest hidden md:table-cell">Contact</th>
                                <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Status</th>
                                <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Acties</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="py-20 text-center">
                                        <Loader2 className="animate-spin text-slate-300 mx-auto mb-2" size={32} />
                                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Klanten laden...</p>
                                    </td>
                                </tr>
                            ) : filteredClients.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="py-20 text-center">
                                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Geen klanten gevonden.</p>
                                    </td>
                                </tr>
                            ) : (
                                filteredClients.map(client => (
                                    <tr
                                        key={client.id}
                                        onClick={() => handleEditClient(client)}
                                        className="cursor-pointer transition-all hover:bg-slate-50 group border-l-4 border-l-transparent hover:border-l-red-300"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-md flex items-center justify-center font-bold text-xs border overflow-hidden shrink-0 bg-slate-100 border-slate-200 text-slate-500">
                                                    {client.logo_url ? (
                                                        <img src={client.logo_url} alt={client.name} className="w-full h-full object-contain" />
                                                    ) : (
                                                        client.name.charAt(0)
                                                    )}
                                                </div>
                                                <div className="min-w-0">
                                                    <h4 className="text-xs font-black leading-tight truncate text-slate-900 group-hover:text-[#e51b23] transition-colors">{client.name}</h4>
                                                    <p className="text-[10px] font-medium truncate max-w-[200px] text-slate-500 flex items-center gap-1">
                                                        <Tag size={10} /> {client.industry || 'Geen industrie'}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        
                                        <td className="px-4 py-3 hidden md:table-cell">
                                            <div className="flex flex-col gap-1 items-start">
                                                <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wide border ${client.client_type === 'Agency' ? 'bg-indigo-50 text-indigo-700 border-indigo-100' : (client.parent_client_id ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-50 text-slate-600 border-slate-200')}`}>
                                                    {client.parent_client_id ? 'Werkplek' : (client.client_type === 'Subcontractor' ? 'Leverancier' : (client.client_type === 'Direct' ? 'Opdrachtgever' : client.client_type))}
                                                </span>
                                                {client.parent_client_id && (
                                                    <span className="text-[10px] font-bold text-slate-500 mt-0.5 flex items-center gap-1">
                                                        ↳ {clients.find(c => c.id === client.parent_client_id)?.name || 'Onbekend'}
                                                    </span>
                                                )}
                                                {client.plan_tier && <span className="text-[10px] font-bold text-slate-500 flex items-center gap-1 mt-0.5">{client.plan_tier} Plan</span>}
                                            </div>
                                        </td>

                                        <td className="px-4 py-3 hidden md:table-cell">
                                            <div className="flex flex-col gap-1">
                                                {((client as any).custom_domain || client.website) ? (
                                                    <span className="text-[11px] font-bold text-slate-700 flex items-center gap-1.5"><Globe size={12} className="text-slate-400" /> {(client as any).custom_domain || client.website}</span>
                                                ) : (
                                                    <span className="text-[11px] font-medium text-slate-400 italic">Geen domein</span>
                                                )}
                                                {client.contact_person && (
                                                    <span className="text-[10px] font-medium text-slate-500 flex items-center gap-1.5"><User size={10} className="text-slate-400" /> {client.contact_person}</span>
                                                )}
                                            </div>
                                        </td>

                                        <td className="px-4 py-3 text-center">
                                            <span className="inline-flex items-center justify-center gap-1.5 bg-emerald-50 px-2 py-1 rounded text-[10px] font-bold text-emerald-700 border border-emerald-100">
                                                Actief
                                            </span>
                                        </td>

                                        <td className="px-4 py-3 text-right">
                                            <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); onLoginAs(client.id, client.name); }}
                                                    className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                                                    title="Inloggen als"
                                                >
                                                    <LogIn size={14} />
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); openBrandingModal(client); }}
                                                    className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                                    title="Branding & Whitelabel"
                                                >
                                                    <Palette size={14} />
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleDeleteClient(client.id); }}
                                                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Verwijderen"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                                <ChevronRight size={14} className="text-slate-400 group-hover:text-red-500 ml-1" />
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
          </div>
        </div>
      </div>

            {/* Client Detail Modal Overlay */}
            {showClientModal && (
                <div className="absolute inset-0 z-50 bg-[#F8FAFC] flex flex-col animate-slideInRight">
                    <ClientDetailModal
                        client={editingClient}
                        accessProfiles={accessProfiles}
                        currentUserRole={user?.role}
                        currentUserClientId={user?.clientId}
                        onClose={() => setShowClientModal(false)}
                        onSaveSuccess={() => { setShowClientModal(false); loadClients(); }}
                    />
                </div>
            )}

            {/* Branding Modal */}
            {showBrandingModal && brandingClient && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden animate-scaleIn flex flex-col max-h-[90vh]">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-purple-50">
                            <div>
                                <h3 className="text-lg font-black text-purple-900 uppercase tracking-tight flex items-center gap-2">
                                    <Palette size={20} className="text-purple-600" /> Branding: {brandingClient.name}
                                </h3>
                                <p className="text-xs text-purple-600/70 font-medium">Stel de look & feel in voor deze klant omgeving.</p>
                            </div>
                            <button onClick={() => setShowBrandingModal(false)} className="text-purple-400 hover:text-purple-700">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Primaire Kleur</label>
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="color"
                                                value={brandingData.primaryColor}
                                                onChange={e => setBrandingData({ ...brandingData, primaryColor: e.target.value })}
                                                className="w-12 h-12 rounded-lg border-0 bg-transparent cursor-pointer"
                                            />
                                            <input
                                                type="text"
                                                value={brandingData.primaryColor}
                                                onChange={e => setBrandingData({ ...brandingData, primaryColor: e.target.value })}
                                                className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-xl font-mono text-sm uppercase"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Secundaire Kleur</label>
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="color"
                                                value={brandingData.secondaryColor}
                                                onChange={e => setBrandingData({ ...brandingData, secondaryColor: e.target.value })}
                                                className="w-12 h-12 rounded-lg border-0 bg-transparent cursor-pointer"
                                            />
                                            <input
                                                type="text"
                                                value={brandingData.secondaryColor}
                                                onChange={e => setBrandingData({ ...brandingData, secondaryColor: e.target.value })}
                                                className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-xl font-mono text-sm uppercase"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Logo</label>

                                        {/* Upload Area */}
                                        <div className="flex gap-3 items-start">
                                            <div className="flex-1">
                                                <input
                                                    type="text"
                                                    value={brandingData.logoUrl || ''}
                                                    onChange={e => setBrandingData({ ...brandingData, logoUrl: e.target.value })}
                                                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm mb-2"
                                                    placeholder="https://..."
                                                />
                                                <div className="relative group">
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleLogoUpload}
                                                        disabled={isSaving}
                                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                                    />
                                                    <div className="flex items-center justify-center gap-2 p-3 bg-slate-100 border-2 border-dashed border-slate-300 rounded-xl text-slate-500 text-xs font-bold uppercase tracking-wide group-hover:bg-slate-200 group-hover:border-slate-400 transition-all">
                                                        <Upload size={14} /> Upload Logo (PNG/SVG)
                                                    </div>
                                                </div>
                                            </div>

                                            <button
                                                onClick={applyMagicColors}
                                                disabled={!brandingData.logoUrl || isSaving}
                                                className="p-3 bg-indigo-50 text-indigo-600 rounded-xl border border-indigo-100 hover:bg-indigo-100 transition-colors flex flex-col items-center gap-1"
                                                title="AI Kleuren bepalen"
                                            >
                                                <Wand2 size={20} />
                                            </button>
                                        </div>
                                        <p className="text-[10px] text-slate-400 mt-1">Upload een logo of plak een directe link.</p>
                                    </div>
                                </div>

                                {/* Live Preview */}
                                <div className="bg-slate-100 rounded-2xl p-4 border border-slate-200 flex flex-col items-center justify-center gap-4 relative overflow-hidden">
                                    <div className="absolute top-0 w-full h-1/2 bg-slate-200/50 -skew-y-12 transform origin-top-left z-0"></div>
                                    <div className="relative z-10 text-center">
                                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Live Preview</p>

                                        {/* Mock Sidebar Button */}
                                        <div
                                            className="w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-lg mx-auto mb-2 transition-colors"
                                            style={{ backgroundColor: brandingData.primaryColor }}
                                        >
                                            <Building2 size={24} />
                                        </div>

                                        {/* Mock Logo */}
                                        {brandingData.logoUrl && (
                                            <div className="mt-4 p-2 bg-white/80 backdrop-blur-sm rounded-lg border border-slate-200 shadow-sm inline-block">
                                                <img src={brandingData.logoUrl} className="h-8 object-contain" alt="Preview" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3">
                            <button onClick={() => setShowBrandingModal(false)} className="px-4 py-2 text-slate-500 font-bold text-sm hover:text-slate-700">Annuleren</button>
                            <button
                                onClick={saveBranding}
                                disabled={isSaving}
                                className="px-6 py-2 bg-purple-600 text-white rounded-lg font-bold text-sm hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                            >
                                {isSaving && <Loader2 size={14} className="animate-spin" />}
                                Branding Opslaan
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientsManager;
